import { useState } from "react";
import { useGameStore } from "../state/useGameStore";
import type { SaveLoad } from "../engine/SaveLoad";

interface QuickMenuProps {
  onRewind: () => void;
  saveLoad: SaveLoad | null;
}

interface MenuButtonProps {
  label: string;
  keyHint?: string;
  active?: boolean;
  onClick: () => void;
}

function MenuButton({ label, keyHint, active, onClick }: MenuButtonProps) {
  return (
    <button
      onClick={onClick}
      className="theme-button"
      style={{
        padding: "0.3rem 0.6rem",
        fontSize: "0.75rem",
        minWidth: "72px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "0.1rem",
        background: active ? "var(--theme-accent, #7ec8e3)" : undefined,
        color: active ? "#111" : undefined,
        border: active ? "1px solid transparent" : undefined,
      }}
    >
      <span>{label}</span>
      {keyHint && (
        <kbd
          style={{
            fontSize: "0.6rem",
            opacity: 0.6,
            fontFamily: "inherit",
            background: "none",
            border: "none",
            padding: 0,
          }}
        >
          {keyHint}
        </kbd>
      )}
    </button>
  );
}

export function QuickMenu({ onRewind, saveLoad }: QuickMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  const skipMode = useGameStore((s) => s.skipMode);
  const autoMode = useGameStore((s) => s.autoMode);
  const skipSeenMode = useGameStore((s) => s.skipSeenMode);
  const hudHidden = useGameStore((s) => s.hudHidden);
  const setSkipMode = useGameStore((s) => s.setSkipMode);
  const toggleAutoMode = useGameStore((s) => s.toggleAutoMode);
  const toggleSkipSeenMode = useGameStore((s) => s.toggleSkipSeenMode);
  const setHudHidden = useGameStore((s) => s.setHudHidden);
  const setOverlay = useGameStore((s) => s.setOverlay);
  const returnToTitle = useGameStore((s) => s.returnToTitle);

  return (
    <div
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "50px",
        zIndex: 60,
        display: "flex",
        alignItems: "flex-end",
      }}
    >
      <div
        className="theme-panel"
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: "0.5rem",
          padding: "0.4rem 1rem",
          borderRadius: 0,
          borderLeft: "none",
          borderRight: "none",
          borderBottom: "none",
          boxShadow: "none",
          transform: isOpen ? "translateY(0)" : "translateY(calc(100% - 6px))",
          transition: "transform 200ms cubic-bezier(0.25, 1, 0.5, 1)",
          flexWrap: "wrap",
        }}
      >
        <MenuButton
          label="Auto"
          keyHint="[A]"
          active={autoMode}
          onClick={toggleAutoMode}
        />
        <MenuButton
          label="Skip"
          keyHint="[Ctrl]"
          active={skipMode}
          onClick={() => setSkipMode(!skipMode)}
        />
        <MenuButton
          label="Skip Seen"
          keyHint="[Tab]"
          active={skipSeenMode}
          onClick={toggleSkipSeenMode}
        />
        <MenuButton
          label="Rewind"
          keyHint="[Q]"
          onClick={onRewind}
        />
        <MenuButton
          label="Log"
          keyHint="[L]"
          onClick={() => setOverlay("backlog")}
        />
        {saveLoad && (
          <MenuButton
            label="Save"
            keyHint="[S]"
            onClick={() => setOverlay("save")}
          />
        )}
        {saveLoad && (
          <MenuButton
            label="Load"
            onClick={() => setOverlay("load")}
          />
        )}
        <MenuButton
          label="Settings"
          onClick={() => setOverlay("settings")}
        />
        <MenuButton
          label="Title"
          onClick={returnToTitle}
        />
        <MenuButton
          label="Hide HUD"
          keyHint="[H]"
          active={hudHidden}
          onClick={() => setHudHidden(!hudHidden)}
        />
      </div>
    </div>
  );
}
